/**
 * CampaignLevelCard Component
 * Collapsible card for managing campaign-level assets (sitelinks, callouts, structured snippets)
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { stripEnumPrefix } from '@/utils/formatEnum';
import type { Campaign } from '@/types/campaign';
import { TextAssetsSection } from '@/components/features/AssetManagement/components/TextAssetsSection';
import type { TextAssetType } from '@/components/features/AssetManagement/types';
import { CAMPAIGN_LEVEL_ID } from '@/components/features/AssetManagement/types';
import { getCampaignLevelSummary, formatCampaignLevelSummary } from './utils';

interface CampaignLevelCardProps {
  campaign: Campaign;
  isExpanded: boolean;
  onToggle: () => void;
  selectedLandingPages: Map<string, boolean>;
}

// Campaign-level asset types
const CAMPAIGN_LEVEL_ASSET_TYPES: TextAssetType[] = ['sitelink', 'callout', 'structured_snippet'];

const ASSET_TYPE_LABELS: Record<TextAssetType, string> = {
  sitelink: 'Sitelinks',
  callout: 'Callouts',
  structured_snippet: 'Structured Snippets',
  headline: 'Headlines',
  description: 'Descriptions',
  long_headline: 'Long Headlines',
};

export function CampaignLevelCard({
  campaign,
  isExpanded,
  onToggle,
  selectedLandingPages,
}: CampaignLevelCardProps) {
  const { memberUuid } = useAuth();
  const queryClient = useQueryClient();

  // Get summary stats
  const summary = getCampaignLevelSummary(campaign);
  const summaryText = formatCampaignLevelSummary(summary);
  const hasAnyAssets = summary.sitelinkCount > 0 || summary.calloutCount > 0 || summary.snippetCount > 0;

  // Normalize campaign type
  const campaignType = stripEnumPrefix(campaign.campaign_type || '');

  // Check if campaign type supports campaign-level assets
  const supportsCampaignLevelAssets = useMemo(() => {
    return (
      campaignType.includes('SEARCH') ||
      campaignType.includes('PERFORMANCE_MAX') ||
      campaignType.includes('SHOPPING') ||
      campaignType.includes('DISPLAY')
    );
  }, [campaignType]);

  // Selected asset type for tab
  const [selectedAssetType, setSelectedAssetType] = useState<TextAssetType>('sitelink');

  // Ref for scroll position preservation
  const headerRef = useRef<HTMLDivElement>(null);

  // Toggle with scroll position preservation
  const handleToggleWithScrollPreserve = useCallback(() => {
    if (!headerRef.current) {
      onToggle();
      return;
    }

    // Capture position before toggle
    const rect = headerRef.current.getBoundingClientRect();
    const topBefore = rect.top;

    // Perform toggle
    onToggle();

    // After DOM update, adjust scroll to keep header in same position
    requestAnimationFrame(() => {
      if (headerRef.current) {
        const topAfter = headerRef.current.getBoundingClientRect().top;
        const diff = topAfter - topBefore;
        if (diff !== 0) {
          window.scrollBy(0, diff);
        }
      }
    });
  }, [onToggle]);

  // Handle keyboard navigation for the header
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggleWithScrollPreserve();
      }
    },
    [handleToggleWithScrollPreserve]
  );

  // Get count for each asset type
  const getAssetCount = (type: TextAssetType): number => {
    switch (type) {
      case 'sitelink':
        return summary.sitelinkCount;
      case 'callout':
        return summary.calloutCount;
      case 'structured_snippet':
        return summary.snippetCount;
      default:
        return 0;
    }
  };

  if (!supportsCampaignLevelAssets) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 focus-within:z-10 relative">
      {/* Section Header */}
      <div
        ref={headerRef}
        role="button"
        tabIndex={0}
        onClick={handleToggleWithScrollPreserve}
        onKeyDown={handleKeyDown}
        className={`
          flex items-center justify-between px-4 py-3 cursor-pointer
          transition-colors focus:outline-none
          ${isExpanded ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}
        `}
        aria-expanded={isExpanded}
        aria-controls="campaign-level-content"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            )}
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              Campaign-Level Assets
            </h3>
          </div>
          <p className="mt-0.5 ml-7 text-xs text-gray-500">
            {hasAnyAssets ? summaryText : 'Add sitelinks, callouts, and structured snippets'}
          </p>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div id="campaign-level-content">
          {/* Tab Bar */}
          <div className="border-b border-gray-200 bg-gray-50">
            <nav className="flex -mb-px" aria-label="Campaign asset tabs">
              {CAMPAIGN_LEVEL_ASSET_TYPES.map((type) => {
                const isActive = selectedAssetType === type;
                const count = getAssetCount(type);

                return (
                  <button
                    key={type}
                    onClick={() => setSelectedAssetType(type)}
                    className={`
                      px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                      ${isActive
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }
                    `}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`panel-${type}`}
                  >
                    {ASSET_TYPE_LABELS[type]}
                    {count > 0 && (
                      <span
                        className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                          isActive
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Panel Content */}
          <div
            role="tabpanel"
            id={`panel-${selectedAssetType}`}
            className="bg-white p-4"
          >
            {memberUuid ? (
              <TextAssetsSection
                campaignId={campaign.campaign_id}
                campaignType={campaignType}
                googleAdsAccountId={campaign.google_ads_account_id || ''}
                entityType="campaign"
                entityId={CAMPAIGN_LEVEL_ID}
                assetType={selectedAssetType}
                memberUuid={memberUuid}
                campaign={campaign}
                selectedLandingPages={selectedLandingPages}
                onAssetsUpdated={() => {
                  queryClient.invalidateQueries({ queryKey: ['campaign'] });
                  queryClient.invalidateQueries({ queryKey: ['app-config'] });
                }}
              />
            ) : (
              <div className="text-center py-6 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-yellow-800">Authentication required to manage assets.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignLevelCard;
